import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiSave, FiExternalLink } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { authAPI, homePageAPI, HomePageContentData } from '../services/api';
import './Dashboard.css';
import './HomePageManagement.css';

interface HomePageManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const emptyCards = (): HomePageContentData['service_cards'] =>
  Array.from({ length: 4 }, () => ({ title: '', items: ['', '', '', ''] }));

const HomePageManagement: React.FC<HomePageManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
  });
  const [data, setData] = useState<HomePageContentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await homePageAPI.getAdmin();
      setData(d);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load home page content');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const run = async () => {
      try {
        const p = await authAPI.getProfile();
        setUserInfo({
          email: p.email || p.username || userEmail || '',
          organization: 'NAATI',
        });
      } catch {
        /* keep props */
      }
    };
    void run();
  }, [userEmail]);

  const setField = <K extends keyof HomePageContentData>(key: K, value: HomePageContentData[K]) => {
    setData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setCardTitle = (ci: number, title: string) => {
    setData((prev) => {
      if (!prev) return prev;
      const cards = [...prev.service_cards];
      cards[ci] = { ...cards[ci], title };
      return { ...prev, service_cards: cards };
    });
  };

  const setCardItem = (ci: number, ii: number, text: string) => {
    setData((prev) => {
      if (!prev) return prev;
      const cards = [...prev.service_cards];
      const items = [...(cards[ci]?.items || ['', '', '', ''])];
      items[ii] = text;
      cards[ci] = { ...cards[ci], title: cards[ci]?.title || '', items };
      return { ...prev, service_cards: cards };
    });
  };

  const handleSave = async () => {
    if (!data || saving) return;
    let payload = { ...data };
    if (!payload.service_cards || payload.service_cards.length !== 4) {
      payload.service_cards = emptyCards();
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await homePageAPI.putAdmin(payload);
      setData(updated);
      setSuccess('Home page saved. Visitors will see changes on next load.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner hp-admin">
            <motion.div
              className="hp-admin-header"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div>
                <h1 className="dashboard-title">Landing page</h1>
                <p className="dashboard-subtitle">
                  Edit texts and links for the public home page (<code>/</code>). Each service block must have
                  exactly four bullet lines (icons on the site stay fixed by position).
                </p>
              </div>
              <div className="hp-admin-actions">
                <a
                  className="hp-admin-link-btn"
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {React.createElement(FiExternalLink as React.ComponentType<{ size?: number }>, { size: 16 })}
                  Open site
                </a>
                <button type="button" className="hp-admin-secondary" onClick={() => void load()} disabled={loading}>
                  {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                    className: loading ? 'hp-spin' : '',
                    size: 16,
                  })}
                  Reload
                </button>
                <button
                  type="button"
                  className="hp-admin-primary"
                  onClick={() => void handleSave()}
                  disabled={loading || saving || !data}
                >
                  {React.createElement(FiSave as React.ComponentType<{ size?: number }>, { size: 16 })}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </motion.div>

            {error && (
              <div className="hp-admin-alert hp-admin-alert-error" role="alert">
                {error}
              </div>
            )}
            {success && <div className="hp-admin-alert hp-admin-alert-success">{success}</div>}

            {loading && !data && (
              <div className="hp-admin-loading">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string }>, {
                  className: 'hp-spin',
                })}
                <span>Loading…</span>
              </div>
            )}

            {data && (
              <div className="hp-admin-form">
                <section className="hp-section">
                  <h2>Hero</h2>
                  <label className="hp-label">
                    Title
                    <input
                      value={data.hero_title}
                      onChange={(e) => setField('hero_title', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  <label className="hp-label">
                    Subtitle
                    <textarea
                      value={data.hero_subtitle}
                      onChange={(e) => setField('hero_subtitle', e.target.value)}
                      className="hp-textarea"
                      rows={2}
                    />
                  </label>
                  <label className="hp-label">
                    CTA button
                    <input
                      value={data.cta_button_label}
                      onChange={(e) => setField('cta_button_label', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                </section>

                <section className="hp-section">
                  <h2>Services block</h2>
                  <label className="hp-label">
                    Section heading
                    <input
                      value={data.services_section_title}
                      onChange={(e) => setField('services_section_title', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  {data.service_cards.map((card, ci) => (
                    <div key={ci} className="hp-card-block">
                      <h3>Card {ci + 1}</h3>
                      <label className="hp-label">
                        Card title
                        <input
                          value={card.title}
                          onChange={(e) => setCardTitle(ci, e.target.value)}
                          className="hp-input"
                        />
                      </label>
                      {[0, 1, 2, 3].map((ii) => (
                        <label key={ii} className="hp-label">
                          Line {ii + 1}
                          <input
                            value={card.items[ii] ?? ''}
                            onChange={(e) => setCardItem(ci, ii, e.target.value)}
                            className="hp-input"
                          />
                        </label>
                      ))}
                    </div>
                  ))}
                </section>

                <section className="hp-section">
                  <h2>About (NAATI)</h2>
                  <label className="hp-label">
                    Section title
                    <input
                      value={data.about_title}
                      onChange={(e) => setField('about_title', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  <label className="hp-label">
                    Body
                    <textarea
                      value={data.about_body}
                      onChange={(e) => setField('about_body', e.target.value)}
                      className="hp-textarea"
                      rows={8}
                    />
                  </label>
                </section>

                <section className="hp-section">
                  <h2>Footer</h2>
                  <label className="hp-label">
                    Tagline (under logo)
                    <textarea
                      value={data.footer_tagline}
                      onChange={(e) => setField('footer_tagline', e.target.value)}
                      className="hp-textarea"
                      rows={3}
                    />
                  </label>
                  <label className="hp-label">
                    Email
                    <input
                      value={data.contact_email}
                      onChange={(e) => setField('contact_email', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  <label className="hp-label">
                    Phone
                    <input
                      value={data.contact_phone}
                      onChange={(e) => setField('contact_phone', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  <label className="hp-label">
                    Address
                    <input
                      value={data.contact_address}
                      onChange={(e) => setField('contact_address', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                  <label className="hp-label">
                    Instagram URL
                    <input
                      value={data.instagram_url}
                      onChange={(e) => setField('instagram_url', e.target.value)}
                      className="hp-input"
                      placeholder="https://…"
                    />
                  </label>
                  <label className="hp-label">
                    Telegram URL
                    <input
                      value={data.telegram_url}
                      onChange={(e) => setField('telegram_url', e.target.value)}
                      className="hp-input"
                      placeholder="https://…"
                    />
                  </label>
                  <label className="hp-label">
                    Copyright line
                    <input
                      value={data.footer_copyright}
                      onChange={(e) => setField('footer_copyright', e.target.value)}
                      className="hp-input"
                    />
                  </label>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePageManagement;
